import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconClose, IconScissors } from './Icons';
import { clipWithLogo, ShareExtras } from './logoBand';
import { longDate } from './dates';

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
type Mode = 'new' | 'move' | 'tl' | 'tr' | 'bl' | 'br';

interface Props {
  imgRef: React.RefObject<HTMLImageElement | null>;
  date: string;
  page: number;
  onCancel: () => void;
  onCut: (blob: Blob) => void;
  onError: (msg: string) => void;
  loadExtras: () => Promise<ShareExtras>; // the day's adverts and flash-news headlines
}

const MIN = 24;

/** Drag over a story, adjust the corners, press the scissors. Works with mouse, finger and pen. */
export function CropLayer({ imgRef, date, onCancel, onCut, onError, loadExtras }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const drag = useRef<{ mode: Mode; sx: number; sy: number; orig: Rect } | null>(null);

  const local = (e: React.PointerEvent) => {
    const b = box.current!.getBoundingClientRect();
    return { x: e.clientX - b.left, y: e.clientY - b.top, W: b.width, H: b.height };
  };
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  const down = (e: React.PointerEvent<HTMLDivElement>) => {
    const p = local(e);
    const target = (e.target as HTMLElement).dataset.h as Mode | undefined;
    let mode: Mode = 'new';
    let orig: Rect = { x: p.x, y: p.y, w: 0, h: 0 };
    if (target && rect) {
      mode = target;
      orig = rect;
    } else if (rect && p.x >= rect.x && p.x <= rect.x + rect.w && p.y >= rect.y && p.y <= rect.y + rect.h) {
      mode = 'move';
      orig = rect;
    }
    drag.current = { mode, sx: p.x, sy: p.y, orig };
    box.current!.setPointerCapture(e.pointerId);
    if (mode === 'new') setRect(null);
    e.preventDefault();
  };

  const move = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const p = local(e);
    const x = clamp(p.x, 0, p.W);
    const y = clamp(p.y, 0, p.H);
    const o = d.orig;
    if (d.mode === 'new') {
      setRect({ x: Math.min(d.sx, x), y: Math.min(d.sy, y), w: Math.abs(x - d.sx), h: Math.abs(y - d.sy) });
    } else if (d.mode === 'move') {
      setRect({ ...o, x: clamp(o.x + (p.x - d.sx), 0, p.W - o.w), y: clamp(o.y + (p.y - d.sy), 0, p.H - o.h) });
    } else {
      // resize from a corner: keep the opposite corner fixed
      const ax = d.mode === 'tl' || d.mode === 'bl' ? o.x + o.w : o.x;
      const ay = d.mode === 'tl' || d.mode === 'tr' ? o.y + o.h : o.y;
      setRect({ x: Math.min(ax, x), y: Math.min(ay, y), w: Math.abs(x - ax), h: Math.abs(y - ay) });
    }
  };

  const up = (e: React.PointerEvent<HTMLDivElement>) => {
    drag.current = null;
    try {
      box.current!.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    setRect((r) => (r && (r.w < MIN || r.h < MIN) ? null : r));
  };

  const cut = () => {
    const img = imgRef.current;
    if (!img || !rect || !box.current) return;
    const ratioX = img.naturalWidth / box.current.clientWidth;
    const ratioY = img.naturalHeight / box.current.clientHeight;
    const sx = Math.round(rect.x * ratioX);
    const sy = Math.round(rect.y * ratioY);
    const sw = Math.max(1, Math.round(rect.w * ratioX));
    const sh = Math.max(1, Math.round(rect.h * ratioY));
    (async () => {
      try {
        // logo band (with the day's adverts), date, flash-news strips and the clipping: built in one place
        onCut(await clipWithLogo(img, sx, sy, sw, sh, undefined, longDate(date))); // a clipping carries only the logo and the date: no advert pictures, no flash / advertisement bars
      } catch {
        onError('Could not cut this page. Reload the page and try again.');
      }
    })();
  };

  // the bar is drawn on <body> so it stays on screen however far the page is scrolled or zoomed
  const bar = (
      <div className="crop-bar" role="toolbar" aria-label="Crop tool">
        <span className="crop-msg">
          <IconScissors size={18} /> {rect ? 'Adjust the corners, then press Cut' : 'Drag over the news you want to cut out'}
        </span>
        <button className="btn" onClick={onCancel}>
          <IconClose size={16} /> Cancel
        </button>
        <button className="btn primary" onClick={cut} disabled={!rect}>
          <IconScissors size={16} /> Cut
        </button>
      </div>
  );

  return (
    <>
      {createPortal(bar, document.body)}
      <div ref={box} className="crop-layer" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        {rect && (
          <div className="crop-sel" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}>
            <i data-h="tl" className="h tl" />
            <i data-h="tr" className="h tr" />
            <i data-h="bl" className="h bl" />
            <i data-h="br" className="h br" />
            <span className="crop-size">
              {Math.round((rect.w * (imgRef.current?.naturalWidth || 1)) / (box.current?.clientWidth || 1))} ×{' '}
              {Math.round((rect.h * (imgRef.current?.naturalHeight || 1)) / (box.current?.clientHeight || 1))}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
